/**
 * Gift box option for the cart.
 * Markup: snippets/cart-gift-box.liquid
 *
 * The gift box LINE ITEM is the only source of truth. Nothing is stored in a
 * cart attribute, localStorage or a hidden field, so there is nothing to
 * reconcile against — which is why removing the line item cannot trigger a
 * re-add. That loop is the defect this replaces in snippets/gift-wrapping.liquid.
 *
 * Listeners are delegated on document because the cart section is destroyed and
 * rebuilt on every refresh. State is re-read from the freshly rendered Liquid
 * each time rather than tracked in JS.
 */
(function () {
  'use strict';

  if (window.ccGiftBoxInitialised) return;
  window.ccGiftBoxInitialised = true;

  var ROOT_SELECTOR = '[data-gift-box]';
  var CHECKBOX_SELECTOR = '[data-gift-box-checkbox]';
  var ERROR_SELECTOR = '[data-gift-box-error]';

  // Matches the pair theme.js swaps after its own ajax add-to-cart, so the
  // header cart count stays in step with the cart body.
  var SWAP_SELECTORS = [
    '#site-control .cart:not(.nav-search)',
    '[data-section-type="cart-template"]'
  ];

  // Backstop against a correction that never takes effect (e.g. the product
  // stops being purchasable mid-session). Reset whenever the shopper acts.
  var MAX_RECONCILE = 3;

  var busy = false;
  var reconcileCount = 0;

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function setBusy(state) {
    busy = state;

    var root = getRoot();
    if (!root) return;

    root.classList.toggle('is-busy', state);

    var checkbox = root.querySelector(CHECKBOX_SELECTOR);
    if (checkbox) checkbox.disabled = state;
  }

  function showError(message) {
    var root = getRoot();
    if (!root) return;

    var el = root.querySelector(ERROR_SELECTOR);
    if (!el) return;

    el.textContent = message;
    el.hidden = false;
  }

  function clearError() {
    var root = getRoot();
    if (!root) return;

    var el = root.querySelector(ERROR_SELECTOR);
    if (!el) return;

    el.textContent = '';
    el.hidden = true;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (response.ok) return response.json();

      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        throw new Error(data.description || data.message || '');
      });
    });
  }

  function addGiftBox(root) {
    return postJSON(root.dataset.cartAddUrl + '.js', {
      items: [{
        id: Number(root.dataset.variantId),
        quantity: 1,
        properties: { _gift_box: 'true' }
      }]
    });
  }

  function changeGiftBox(root, key, quantity) {
    return postJSON(root.dataset.cartChangeUrl + '.js', {
      id: key,
      quantity: quantity
    });
  }

  function refreshCart(cartUrl) {
    return fetch(cartUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) throw new Error('');
      return response.text();
    }).then(function (html) {
      var incomingDoc = new DOMParser().parseFromString(html, 'text/html');

      SWAP_SELECTORS.forEach(function (selector) {
        var incoming = incomingDoc.querySelector(selector);
        var current = document.querySelector(selector);
        if (!incoming || !current) return;

        // Theme animations keep elements hidden until scrolled into view.
        // Freshly injected markup would never trigger, so strip the hooks —
        // this mirrors what theme.js does after its own ajax add-to-cart.
        incoming.removeAttribute('data-cc-animate');
        Array.prototype.forEach.call(
          incoming.querySelectorAll('[data-cc-animate]'),
          function (el) { el.removeAttribute('data-cc-animate'); }
        );

        current.parentNode.replaceChild(incoming, current);
      });
    });
  }

  /**
   * Silent corrections, applied after load and after every refresh:
   *   - a cart holding nothing but the gift box loses it
   *   - a quantity above 1 is pulled back to 1
   * Both run through the same ajax path as a shopper click, so neither reloads
   * the page. Each converges in one pass: once corrected, the re-rendered
   * markup no longer meets the condition.
   */
  function reconcile() {
    if (busy) return;
    if (reconcileCount >= MAX_RECONCILE) return;

    var root = getRoot();
    if (!root) return;

    var key = root.dataset.lineKey;
    if (!key) return;

    var target = null;

    if (root.dataset.onlyItem === 'true') {
      target = 0;
    } else if ((parseInt(root.dataset.lineQuantity, 10) || 0) > 1) {
      target = 1;
    }

    if (target === null) return;

    reconcileCount += 1;
    setBusy(true);

    changeGiftBox(root, key, target)
      .then(function () {
        return refreshCart(root.dataset.cartUrl);
      })
      .then(function () {
        setBusy(false);
        reconcile();
      })
      .catch(function () {
        setBusy(false);
      });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var checkbox = target.closest(CHECKBOX_SELECTOR);
    if (!checkbox) return;

    var root = checkbox.closest(ROOT_SELECTOR);
    if (!root) return;

    // A second toggle while a request is in flight is discarded, not queued.
    if (busy) {
      checkbox.checked = !checkbox.checked;
      return;
    }

    var wanted = checkbox.checked;
    var key = root.dataset.lineKey;
    var cartUrl = root.dataset.cartUrl;
    var errorText = root.dataset.errorText;
    var refreshErrorText = root.dataset.refreshErrorText;

    clearError();
    setBusy(true);
    reconcileCount = 0;

    var request;
    if (wanted) {
      request = addGiftBox(root);
    } else if (key) {
      request = changeGiftBox(root, key, 0);
    } else {
      // Already absent — nothing to remove.
      request = Promise.resolve();
    }

    request.then(function () {
      // The cart changed. From here the checkbox must never be reverted, or the
      // UI would contradict the server.
      return refreshCart(cartUrl).then(function () {
        setBusy(false);
        reconcile();
      }, function () {
        setBusy(false);
        showError(refreshErrorText);
      });
    }, function (error) {
      // The cart did NOT change. Put the checkbox back where the shopper
      // found it and say so.
      setBusy(false);

      var current = getRoot();
      var currentCheckbox = current && current.querySelector(CHECKBOX_SELECTOR);
      if (currentCheckbox) currentCheckbox.checked = !wanted;

      showError(error && error.message ? error.message : errorText);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
